import { createContext, type ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { apiFetch, fetchMe, type Me } from './api';

interface SessionState {
  /** `undefined` while loading, `null` when signed out. */
  me: Me | null | undefined;
  /**
   * Whether this visitor sees MOGZO's public side (M28): everyone after the launch, only staff
   * while coming soon is on. `undefined` while loading.
   */
  publicSide: boolean | undefined;
  /** Whether coming soon is on: public communities are read-only until the launch. */
  comingSoon: boolean | undefined;
  /** Re-reads the account after sign-in, sign-out or a profile change. */
  refresh: () => Promise<Me | null>;
}

const SessionContext = createContext<SessionState | null>(null);

/** The coming-soon switch for this visitor; closed when the API can't say. */
function fetchSite(): Promise<{ comingSoon: boolean; seesPublicSide: boolean }> {
  return apiFetch<{ comingSoon: boolean; seesPublicSide: boolean }>('/v1/site').catch(() => ({
    comingSoon: true,
    seesPublicSide: false,
  }));
}

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null | undefined>(undefined);
  const [site, setSite] = useState<{ comingSoon: boolean; seesPublicSide: boolean }>();

  const refresh = useCallback(async () => {
    let next: Me | null;
    try {
      next = await fetchMe();
    } catch {
      // API unreachable: treat as signed out rather than spinning forever.
      next = null;
    }
    // Who's signed in decides it (staff see the public side early), so it's read after.
    setSite(await fetchSite());
    setMe(next);
    return next;
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  return (
    <SessionContext
      value={{ me, publicSide: site?.seesPublicSide, comingSoon: site?.comingSoon, refresh }}
    >
      {children}
    </SessionContext>
  );
}

export function useSession(): SessionState {
  const session = useContext(SessionContext);
  if (!session) throw new Error('useSession must be used inside <SessionProvider>');
  return session;
}
